import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import { useUserStore } from '../stores/useUserStore';
import { shiftService, Shift as ApiShift } from '../services/shiftService';
import { usersService } from '../services/usersService';
import { startOfWeek, endOfWeek, addDays, format, getDay, isSameDay, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { useRBAC } from '../hooks/useRBAC';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { AppleCalendar } from '../components/ui/apple-calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Calendar as CalendarIcon,
  Clock,
  Plus,
  X,
  AlertTriangle,
  CheckCircle,
  Search,
  Settings,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Calendar,
  CalendarRange,
  Trash2,
} from 'lucide-react';

interface Shift {
  id: string;
  userId: string;
  userName: string;
  date: string;
  startTime: string;
  endTime: string;
  position: string;
  location: string;
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled';
  notes?: string;
  isRecurring: boolean;
  recurringPattern?: 'weekly' | 'biweekly' | 'monthly';
  createdAt: string;
  updatedAt: string;
}

interface StaffMember {
  id: string;
  name: string;
  email: string;
  position: string;
  roles?: string[]; // Store user roles for filtering
  maxHoursPerWeek: number;
  preferredShifts: string[];
  unavailableDates: string[];
  skills: string[];
  isActive: boolean;
}

interface ScheduleConflict {
  id: string;
  type: 'overlap' | 'overtime' | 'unavailable' | 'skill_mismatch';
  shiftId: string;
  staffId: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  suggestedResolution?: string;
}

// Role hierarchy for filtering users
const ROLE_HIERARCHY: Record<string, number> = {
  sysAdmin: 5,
  admin: 4,
  orgAdmin: 4,
  storeManager: 3,
  staff: 2,
  cashier: 1,
};

// Helper function to get user's highest role level
const getUserHighestRoleLevel = (roles: string[] = []): number => {
  if (!roles || roles.length === 0) return 0;
  return Math.max(...roles.map((role) => ROLE_HIERARCHY[role] || 0));
};

// Helper function to check if a role should be visible to the current user
const shouldShowRole = (userRole: string, viewerRoleLevel: number): boolean => {
  const roleLevel = ROLE_HIERARCHY[userRole] || 0;

  // sysAdmin can see all roles
  if (viewerRoleLevel >= 5) return true;

  // orgAdmin/admin can see all except sysAdmin
  if (viewerRoleLevel >= 4) {
    return roleLevel < 5;
  }

  // storeManager can see all except sysAdmin and orgAdmin/admin
  if (viewerRoleLevel >= 3) {
    return roleLevel < 4;
  }

  // staff can see all except sysAdmin, orgAdmin/admin, and storeManager
  if (viewerRoleLevel >= 2) {
    return roleLevel < 3;
  }

  // cashier can see all except sysAdmin, orgAdmin/admin, storeManager, and staff
  if (viewerRoleLevel >= 1) {
    return roleLevel < 2;
  }

  return false;
};

// Filter staff members based on role hierarchy
const filterStaffByRole = (
  staff: StaffMember[],
  viewerRoles: string[] = []
): StaffMember[] => {
  const viewerRoleLevel = getUserHighestRoleLevel(viewerRoles);

  return staff.filter((member) => {
    // If staff member has no roles, show them (they might not have been assigned roles yet)
    if (!member.roles || member.roles.length === 0) return true;

    // Check if any of the staff member's roles should be visible
    return member.roles.some((role) => shouldShowRole(role, viewerRoleLevel));
  });
};

const ModernShiftCalendar: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { user: currentUser } = useUserStore();
  const { userRoles } = useRBAC();

  const [shifts, setShifts] = useState<Shift[]>([]);
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [conflicts, setConflicts] = useState<ScheduleConflict[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day' | 'year'>(
    'month'
  );
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPosition, setFilterPosition] = useState('all');
  const [staffSearchOpen, setStaffSearchOpen] = useState(false);
  const [staffSearchValue, setStaffSearchValue] = useState('');
  const [editStaffSearchOpen, setEditStaffSearchOpen] = useState(false);
  const [editStaffSearchValue, setEditStaffSearchValue] = useState('');

  // Form state for creating/editing shifts
  const [shiftForm, setShiftForm] = useState({
    userId: '',
    date: '',
    startTime: '',
    endTime: '',
    position: '',
    location: '',
    notes: '',
    isRecurring: false,
    recurringPattern: 'weekly' as 'weekly' | 'biweekly' | 'monthly',
  });

  useEffect(() => {
    if (currentUser) {
      loadShifts();
      loadStaffMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (shifts.length > 0) {
      detectConflicts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shifts]);

  // Handle Shift+Delete keyboard shortcut to show delete confirmation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if Shift+Delete is pressed and edit dialog is open
      if (
        e.shiftKey &&
        (e.key === 'Delete' || e.key === 'Del' || e.keyCode === 46) &&
        showEditDialog &&
        editingShift
      ) {
        e.preventDefault();
        e.stopPropagation();
        setShowDeleteDialog(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showEditDialog, editingShift]);

  // Transform API shift to component shift format
  const transformShift = (apiShift: ApiShift): Shift => {
    // Parse the ISO string and extract date/time components in UTC to avoid timezone shifts
    const startTimeStr = typeof apiShift.startTime === 'string' ? apiShift.startTime : apiShift.startTime.toISOString();
    const endTimeStr = typeof apiShift.endTime === 'string' ? apiShift.endTime : apiShift.endTime.toISOString();

    // Extract date part from ISO string (YYYY-MM-DD) to avoid timezone conversion
    const getDateFromISO = (isoString: string): string => {
      // ISO string format: "2024-12-15T06:30:00.000Z" or "2024-12-15T06:30:00+07:00"
      // Extract the date part directly from the string before 'T'
      const datePart = isoString.split('T')[0];
      return datePart;
    };

    // Extract time part in local timezone to match what the user selected
    const getTimeFromISO = (isoString: string): string => {
      const date = new Date(isoString);
      // Use local time methods to get time components (not UTC)
      // This ensures the time matches what the user selected in their timezone
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    return {
      id: apiShift.id,
      userId: apiShift.staffId,
      userName: apiShift.staffName,
      date: getDateFromISO(startTimeStr),
      startTime: getTimeFromISO(startTimeStr),
      endTime: getTimeFromISO(endTimeStr),
      position: apiShift.position || 'Staff',
      location: (apiShift as any).location || '', // Get location from API if available
      status: apiShift.status,
      notes: apiShift.description,
      isRecurring: apiShift.isRecurring,
      recurringPattern:
        apiShift.recurringPattern?.frequency === 'weekly'
          ? 'weekly'
          : apiShift.recurringPattern?.frequency === 'monthly'
          ? 'monthly'
          : undefined,
      createdAt: apiShift.createdAt || new Date().toISOString(),
      updatedAt: apiShift.updatedAt || new Date().toISOString(),
    };
  };

  const loadShifts = async () => {
    setLoading(true);
    try {
      const allShifts = await shiftService.getShifts();
      const transformedShifts = allShifts.map(transformShift);
      
      // Apply visibility restrictions for staff
      let finalShifts = transformedShifts;
      
      const isStaff = userRoles && 
        !userRoles.includes('admin') && 
        !userRoles.includes('sysAdmin') && 
        !userRoles.includes('orgAdmin') && 
        !userRoles.includes('storeManager');

      if (isStaff) {
        const today = new Date();
        const isSunday = today.getDay() === 0;
        
        // Allowed start is start of week (Sunday-based) to hide previous Saturday
        const allowedStartDate = startOfWeek(today, { weekStartsOn: 0 });
        const allowedStartDateStr = format(allowedStartDate, 'yyyy-MM-dd');
        
        // Allowed end is end of week (Monday-based) to include next Sunday
        const currentWeekEnd = endOfWeek(today, { weekStartsOn: 1 });
        let requestEndDate = currentWeekEnd;
        
        if (isSunday) {
          requestEndDate = addDays(currentWeekEnd, 7);
        }
        
        const allowedEndDateStr = format(requestEndDate, 'yyyy-MM-dd');
        
        finalShifts = transformedShifts.filter(shift => {
          // Always show user's own shifts
          if (shift.userId === currentUser?.id) return true;
          
          // String comparison works correctly for YYYY-MM-DD
          return shift.date >= allowedStartDateStr && shift.date <= allowedEndDateStr;
        });
      }
      
      setShifts(finalShifts);
    } catch (error) {
      console.error('Error loading shifts:', error);
      notify('Failed to load shifts', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadStaffMembers = async () => {
    try {
      // Fetch all users/staff members from the API
      const users = await usersService.getUsers({
        page: 1,
        size: 1000, // Get all staff members
      });

      const usersArray = Array.isArray(users) ? users : [];

      // Map users to StaffMember format
      const staffMembersList: StaffMember[] = usersArray.map((user) => {
        // Construct name from firstName and lastName, with fallbacks
        const name =
          (user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.firstName || user.lastName) ||
          user.username ||
          user.email ||
          'Unknown';

        return {
          id: user.id,
          name,
          email: user.email || '',
          position: (user as any).position || (user as any).role || 'Staff',
          roles: user.roles || [], // Preserve user roles for filtering
          maxHoursPerWeek: 40,
          preferredShifts: [],
          unavailableDates: [],
          skills: [],
          isActive: (user as any).isActive !== false, // Default to true if not specified
        };
      });

      setStaffMembers(staffMembersList);
    } catch (error) {
      console.error('Error loading staff members:', error);
      notify('Failed to load staff members', { type: 'error' });
      // Fallback: try to extract from shifts if API fails
      try {
        const shifts = await shiftService.getShifts();
        const staffMap = new Map<string, StaffMember>();

        shifts.forEach((shift) => {
          if (!staffMap.has(shift.staffId)) {
            staffMap.set(shift.staffId, {
              id: shift.staffId,
              name: shift.staffName,
              email: '',
              position: shift.position || 'Staff',
              roles: [], // No role info available from shifts fallback
              maxHoursPerWeek: 40,
              preferredShifts: [],
              unavailableDates: [],
              skills: [],
              isActive: true,
            });
          }
        });

        setStaffMembers(Array.from(staffMap.values()));
      } catch (fallbackError) {
        console.error(
          'Error loading staff from shifts fallback:',
          fallbackError
        );
      }
    }
  };

  // Filter staff members by role
  const roleFilteredStaffMembers = useMemo(() => {
    return filterStaffByRole(staffMembers, currentUser?.roles || []);
  }, [staffMembers, currentUser?.roles]);

  // Filter staff members by search (for create dialog)
  const filteredStaffMembers = useMemo(() => {
    if (!staffSearchValue.trim()) {
      return roleFilteredStaffMembers;
    }

    const searchLower = staffSearchValue.toLowerCase();
    return roleFilteredStaffMembers.filter(
      (staff) =>
        staff.name.toLowerCase().includes(searchLower) ||
        staff.email.toLowerCase().includes(searchLower) ||
        staff.position.toLowerCase().includes(searchLower)
    );
  }, [roleFilteredStaffMembers, staffSearchValue]);

  // Filter staff members by search (for edit dialog)
  const filteredStaffMembersForEdit = useMemo(() => {
    if (!editStaffSearchValue.trim()) {
      return roleFilteredStaffMembers;
    }

    const searchLower = editStaffSearchValue.toLowerCase();
    return roleFilteredStaffMembers.filter(
      (staff) =>
        staff.name.toLowerCase().includes(searchLower) ||
        staff.email.toLowerCase().includes(searchLower) ||
        staff.position.toLowerCase().includes(searchLower)
    );
  }, [roleFilteredStaffMembers, editStaffSearchValue]);

  // Reset search values when dialogs close
  useEffect(() => {
    if (!showCreateDialog) {
      setStaffSearchValue('');
      setStaffSearchOpen(false);
    } else {
      // Initialize date field with selectedDate when dialog opens
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      setShiftForm((prev) => ({
        ...prev,
        date: dateStr, // Always set to selectedDate when dialog opens
      }));
    }
  }, [showCreateDialog, selectedDate]);

  useEffect(() => {
    if (!showEditDialog) {
      setEditStaffSearchValue('');
      setEditStaffSearchOpen(false);
    }
  }, [showEditDialog]);

  const getConflictDescription = (type: string) => {
    switch (type) {
      case 'overlap':
        return translate(
          'pos.staff.schedule.conflict_overlap_description',
          'Shift overlaps with existing schedule'
        );
      case 'overtime':
        return translate(
          'pos.staff.schedule.conflict_overtime_description',
          'Staff member would exceed weekly hour limit'
        );
      case 'unavailable':
        return translate(
          'pos.staff.schedule.conflict_unavailable_description',
          'Staff member is unavailable on this date'
        );
      case 'skill_mismatch':
        return translate(
          'pos.staff.schedule.conflict_skill_mismatch_description',
          'Staff member lacks required skills for this position'
        );
      default:
        return '';
    }
  };

  const getConflictResolution = (type: string) => {
    switch (type) {
      case 'overlap':
        return translate(
          'pos.staff.schedule.conflict_overlap_resolution',
          'Adjust start or end time'
        );
      case 'overtime':
        return translate(
          'pos.staff.schedule.conflict_overtime_resolution',
          'Reduce hours or assign to different staff'
        );
      case 'unavailable':
        return translate(
          'pos.staff.schedule.conflict_unavailable_resolution',
          'Assign to different staff member or change date'
        );
      case 'skill_mismatch':
        return translate(
          'pos.staff.schedule.conflict_skill_mismatch_resolution',
          'Assign to qualified staff or change position'
        );
      default:
        return '';
    }
  };

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case 'critical':
        return translate('pos.staff.schedule.severity_critical', 'Critical');
      case 'high':
        return translate('pos.staff.schedule.severity_high', 'High');
      case 'medium':
        return translate('pos.staff.schedule.severity_medium', 'Medium');
      case 'low':
        return translate('pos.staff.schedule.severity_low', 'Low');
      default:
        return severity;
    }
  };

  const detectConflicts = () => {
    // TODO: Implement real conflict detection logic
    // For now, return empty array - conflicts can be detected by comparing shifts
    const detectedConflicts: ScheduleConflict[] = [];

    // Check for overlapping shifts for the same staff member
    shifts.forEach((shift, index) => {
      const shiftStart = new Date(`${shift.date}T${shift.startTime}`);
      const shiftEnd = new Date(`${shift.date}T${shift.endTime}`);

      shifts.slice(index + 1).forEach((otherShift) => {
        if (shift.userId === otherShift.userId) {
          const otherStart = new Date(
            `${otherShift.date}T${otherShift.startTime}`
          );
          const otherEnd = new Date(`${otherShift.date}T${otherShift.endTime}`);

          // Check for overlap
          if (
            (shiftStart >= otherStart && shiftStart < otherEnd) ||
            (shiftEnd > otherStart && shiftEnd <= otherEnd) ||
            (shiftStart <= otherStart && shiftEnd >= otherEnd)
          ) {
            detectedConflicts.push({
              id: `conflict-${shift.id}-${otherShift.id}`,
              type: 'overlap',
              shiftId: shift.id,
              staffId: shift.userId,
              description: 'Shift overlaps with existing schedule',
              severity: 'high',
              suggestedResolution: 'Adjust start or end time',
            });
          }
        }
      });
    });

    setConflicts(detectedConflicts);
  };

  const getShiftsForDate = (date: Date | undefined) => {
    if (!date) return [];
    // Format date in local timezone to avoid UTC conversion shifting the date
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    return shifts.filter((shift) => shift.date === dateString);
  };

  const getWeekStart = (date: Date) => {
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
    return startOfWeek;
  };

  const getShiftsForWeek = (date: Date) => {
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    return shifts.filter((shift) => {
      const shiftDate = new Date(shift.date);
      return shiftDate >= startOfWeek && shiftDate <= endOfWeek;
    });
  };

  const getShiftsForMonth = (date: Date) => {
    const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    return shifts.filter((shift) => {
      const shiftDate = new Date(shift.date);
      return shiftDate >= startOfMonth && shiftDate <= endOfMonth;
    });
  };

  const filteredShifts = shifts.filter((shift) => {
    const matchesSearch =
      shift.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shift.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shift.location.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPosition =
      filterPosition === 'all' || shift.position === filterPosition;

    return matchesSearch && matchesPosition;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'confirmed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-gray-500" />;
      case 'cancelled':
        return <X className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCreateShift = async () => {
    if (!shiftForm.date || !shiftForm.startTime || !shiftForm.endTime) {
      notify(
        'Please fill in all required fields (date, start time, end time)',
        { type: 'error' }
      );
      return;
    }

    try {
      const selectedStaff = shiftForm.userId
        ? staffMembers.find((s) => s.id === shiftForm.userId)
        : null;
      // Create ISO date-time strings that preserve the selected date exactly
      // Format: YYYY-MM-DDTHH:mm:ss with timezone offset to preserve the date
      const formatDateTimeWithTimezone = (
        dateStr: string,
        timeStr: string
      ): string => {
        const [hours, minutes] = timeStr.split(':');
        // Get timezone offset in minutes
        const now = new Date();
        const offsetMinutes = now.getTimezoneOffset();
        const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
        const offsetMins = Math.abs(offsetMinutes) % 60;
        const offsetSign = offsetMinutes <= 0 ? '+' : '-';
        const offsetStr = `${offsetSign}${String(offsetHours).padStart(
          2,
          '0'
        )}:${String(offsetMins).padStart(2, '0')}`;

        // Construct ISO string with timezone: YYYY-MM-DDTHH:mm:ss+HH:mm
        return `${dateStr}T${hours}:${minutes}:00${offsetStr}`;
      };

      const newShift = await shiftService.createShift({
        title: shiftForm.position || 'Shift',
        startTime: formatDateTimeWithTimezone(
          shiftForm.date,
          shiftForm.startTime
        ),
        endTime: formatDateTimeWithTimezone(shiftForm.date, shiftForm.endTime),
        staffId: shiftForm.userId || '',
        staffName: selectedStaff?.name || '',
        position: shiftForm.position || undefined,
        status: 'scheduled',
        description: shiftForm.notes,
        ...(shiftForm.location ? { location: shiftForm.location } as any : {}),
        isRecurring: shiftForm.isRecurring,
        recurringPattern: shiftForm.isRecurring
          ? {
              frequency:
                shiftForm.recurringPattern === 'weekly'
                  ? 'weekly'
                  : shiftForm.recurringPattern === 'monthly'
                  ? 'monthly'
                  : 'weekly',
              interval: 1,
            }
          : undefined,
      });

      // Reload shifts to get the updated list
      await loadShifts();
      setShowCreateDialog(false);
      setShiftForm({
        userId: '',
        date: '',
        startTime: '',
        endTime: '',
        position: '',
        location: '',
        notes: '',
        isRecurring: false,
        recurringPattern: 'weekly',
      });

      notify('Shift created successfully!', { type: 'success' });
    } catch (error) {
      console.error('Error creating shift:', error);
      notify('Failed to create shift', { type: 'error' });
    }
  };

  const handleEditShift = (shift: Shift) => {
    setEditingShift(shift);
    setShiftForm({
      userId: shift.userId,
      date: shift.date,
      startTime: shift.startTime,
      endTime: shift.endTime,
      position: shift.position,
      location: shift.location,
      notes: shift.notes || '',
      isRecurring: shift.isRecurring,
      recurringPattern: shift.recurringPattern || 'weekly',
    });
    setShowEditDialog(true);
  };

  const handleUpdateShift = async () => {
    if (!editingShift) return;

    try {
      const selectedStaff = shiftForm.userId
        ? staffMembers.find((s) => s.id === shiftForm.userId)
        : null;
      // Create ISO date-time strings that preserve the selected date exactly
      // Format: YYYY-MM-DDTHH:mm:ss with timezone offset to preserve the date
      const formatDateTimeWithTimezone = (
        dateStr: string,
        timeStr: string
      ): string => {
        const [hours, minutes] = timeStr.split(':');
        // Get timezone offset in minutes
        const now = new Date();
        const offsetMinutes = now.getTimezoneOffset();
        const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
        const offsetMins = Math.abs(offsetMinutes) % 60;
        const offsetSign = offsetMinutes <= 0 ? '+' : '-';
        const offsetStr = `${offsetSign}${String(offsetHours).padStart(
          2,
          '0'
        )}:${String(offsetMins).padStart(2, '0')}`;

        // Construct ISO string with timezone: YYYY-MM-DDTHH:mm:ss+HH:mm
        return `${dateStr}T${hours}:${minutes}:00${offsetStr}`;
      };

      await shiftService.updateShift(editingShift.id, {
        title: shiftForm.position || 'Shift',
        startTime: formatDateTimeWithTimezone(
          shiftForm.date,
          shiftForm.startTime
        ),
        endTime: formatDateTimeWithTimezone(shiftForm.date, shiftForm.endTime),
        staffId: shiftForm.userId || '',
        staffName: selectedStaff?.name || '',
        position: shiftForm.position || undefined,
        description: shiftForm.notes,
        ...(shiftForm.location ? { location: shiftForm.location } as any : {}),
        isRecurring: shiftForm.isRecurring,
        recurringPattern: shiftForm.isRecurring
          ? {
              frequency:
                shiftForm.recurringPattern === 'weekly'
                  ? 'weekly'
                  : shiftForm.recurringPattern === 'monthly'
                  ? 'monthly'
                  : 'weekly',
              interval: 1,
            }
          : undefined,
      });

      // Reload shifts to get the updated list
      await loadShifts();
      setShowEditDialog(false);
      setEditingShift(null);

      notify('Shift updated successfully!', { type: 'success' });
    } catch (error) {
      console.error('Error updating shift:', error);
      notify('Failed to update shift', { type: 'error' });
    }
  };

  const handleDeleteShift = async (shiftId: string) => {
    try {
      await shiftService.deleteShift(shiftId);
      // Reload shifts to get the updated list
      await loadShifts();
      // Close edit dialog if it's open
      if (showEditDialog) {
        setShowEditDialog(false);
        setEditingShift(null);
      }
      notify('Shift deleted successfully!', { type: 'success' });
    } catch (error) {
      console.error('Error deleting shift:', error);
      notify('Failed to delete shift', { type: 'error' });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeString: string) => {
    return timeString;
  };

  const renderDayContent = (date: Date | undefined) => {
    if (!date) return null;
    const dayShifts = getShiftsForDate(date);
    const isToday = date.toDateString() === new Date().toDateString();
    const isSelected = selectedDate.toDateString() === date.toDateString();

    return (
      <div className="relative h-full w-full flex flex-col">
        <div
          className={`text-sm font-medium mb-1 ${
            isToday ? 'text-blue-600 font-bold' : ''
          } ${isSelected ? 'text-white' : ''}`}
        >
          {date.getDate()}
        </div>
        {dayShifts.length > 0 && (
          <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
            {dayShifts.slice(0, 2).map((shift) => (
              <div
                key={shift.id}
                className={`text-xs px-1 py-0.5 rounded truncate ${
                  shift.status === 'confirmed'
                    ? 'bg-green-100 text-green-800'
                    : shift.status === 'scheduled'
                    ? 'bg-blue-100 text-blue-800'
                    : shift.status === 'cancelled'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-gray-100 text-gray-800'
                } ${shift.userId === currentUser?.id ? 'border border-green-500 font-medium ring-1 ring-green-200' : ''}`}
                title={`${shift.userName} - ${shift.startTime}-${shift.endTime}${shift.userId === currentUser?.id ? ' (You)' : ''}`}
              >
                {shift.startTime} - {shift.endTime}
              </div>
            ))}
            {dayShifts.length > 2 && (
              <div className="text-xs text-muted-foreground truncate">
                {translate(
                  'pos.staff.schedule.month_more_label',
                  '+%{count} more'
                ).replace('%{count}', `${dayShifts.length - 2}`)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {translate(
              'pos.staff.shift_planning.loading',
              'Loading shift calendar...'
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      {/* Schedule Conflicts panel removed - conflicts are now highlighted directly on shifts */}

      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            {translate(
              'pos.staff.schedule.controls_title',
              'Calendar Controls'
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <Label htmlFor="search">
                {translate('pos.staff.schedule.search_label', 'Search shifts')}
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder={translate(
                    'pos.staff.schedule.search_placeholder',
                    'Search by staff, position, location...'
                  )}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="viewMode">
                {translate('pos.staff.schedule.view_mode_label', 'View Mode')}
              </Label>
              <div className="flex items-center border rounded-lg p-1">
                <Button
                  variant={viewMode === 'day' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('day')}
                  className="h-8"
                >
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {translate('pos.staff.timesheet.view_day', 'Day')}
                </Button>
                <Button
                  variant={viewMode === 'week' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('week')}
                  className="h-8"
                >
                  <CalendarDays className="h-4 w-4 mr-2" />
                  {translate('pos.staff.timesheet.view_week', 'Week')}
                </Button>
                <Button
                  variant={viewMode === 'month' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('month')}
                  className="h-8"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  {translate('pos.staff.timesheet.view_month', 'Month')}
                </Button>
                <Button
                  variant={viewMode === 'year' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('year')}
                  className="h-8"
                >
                  <CalendarRange className="h-4 w-4 mr-2" />
                  {translate('pos.staff.timesheet.view_year', 'Year')}
                </Button>
              </div>
            </div>
            <div>
              <Label htmlFor="position">
                {translate('pos.staff.schedule.position_label', 'Position')}
              </Label>
              <Select value={filterPosition} onValueChange={setFilterPosition}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={translate(
                      'pos.staff.schedule.position_label',
                      'Filter by position'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {translate(
                      'pos.staff.schedule.position_all',
                      'All Positions'
                    )}
                  </SelectItem>
                  <SelectItem value="Barista">Barista</SelectItem>
                  <SelectItem value="Cashier">Cashier</SelectItem>
                  <SelectItem value="Manager">Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendar View */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            {viewMode === 'month'
              ? translate(
                  'pos.staff.schedule.calendar_title_prefix_monthly',
                  'Monthly Schedule'
                )
              : viewMode === 'week'
              ? translate(
                  'pos.staff.schedule.calendar_title_prefix_weekly',
                  'Weekly Schedule'
                )
              : viewMode === 'day'
              ? translate(
                  'pos.staff.schedule.calendar_title_prefix_daily',
                  'Daily Schedule'
                )
              : translate(
                  'pos.staff.schedule.calendar_title_prefix_yearly',
                  'Yearly Schedule'
                )}
          </CardTitle>
          <CardDescription>
            {viewMode === 'month'
              ? translate(
                  'pos.staff.schedule.calendar_description_month',
                  'View all shifts for the month'
                )
              : viewMode === 'week'
              ? translate(
                  'pos.staff.schedule.calendar_description_week',
                  'View shifts for the week'
                )
              : viewMode === 'day'
              ? translate(
                  'pos.staff.schedule.calendar_description_day',
                  'View shifts for the selected day'
                )
              : translate(
                  'pos.staff.schedule.calendar_description_year',
                  'View all shifts for the year'
                )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar */}
            <div className="lg:col-span-2">
              {viewMode === 'year' ? (
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newDate = new Date(selectedDate);
                          newDate.setFullYear(newDate.getFullYear() - 1);
                          setSelectedDate(newDate);
                        }}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        {translate(
                          'pos.staff.schedule.nav_previous_year',
                          'Previous Year'
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedDate(new Date())}
                      >
                        {translate(
                          'pos.staff.schedule.nav_this_year',
                          'This Year'
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newDate = new Date(selectedDate);
                          newDate.setFullYear(newDate.getFullYear() + 1);
                          setSelectedDate(newDate);
                        }}
                      >
                        {translate(
                          'pos.staff.schedule.nav_next_year',
                          'Next Year'
                        )}
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold">
                      {translate(
                        'pos.staff.schedule.year_view_title',
                        'Year View'
                      )}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {translate(
                        'pos.staff.schedule.year_view_subtitle',
                        'All shifts for %{year}'
                      ).replace('%{year}', `${selectedDate.getFullYear()}`)}
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {Array.from({ length: 12 }, (_, i) => {
                      const monthDate = new Date(
                        selectedDate.getFullYear(),
                        i,
                        1
                      );
                      const monthShifts = getShiftsForMonth(monthDate);
                      return (
                        <div
                          key={i}
                          className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            setSelectedDate(monthDate);
                            setShowCreateDialog(true);
                          }}
                        >
                          <h4 className="font-medium text-sm mb-2">
                            {monthDate.toLocaleDateString(undefined, {
                              month: 'long',
                            })}
                          </h4>
                          <div className="space-y-1">
                            {monthShifts.slice(0, 3).map((shift) => (
                              <div
                                key={shift.id}
                                className={`text-xs p-1 rounded cursor-pointer ${shift.userId === currentUser?.id ? 'bg-green-100 hover:bg-green-200 border border-green-300' : 'bg-blue-50 hover:bg-blue-100'}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditShift(shift);
                                }}
                              >
                                {shift.userName}{shift.userId === currentUser?.id ? ' (You)' : ''} - {shift.startTime} -{' '}
                                {shift.endTime}
                              </div>
                            ))}
                            {monthShifts.length > 3 && (
                              <div className="text-xs text-gray-500">
                                {translate(
                                  'pos.staff.schedule.month_more_label',
                                  '+%{count} more'
                                ).replace(
                                  '%{count}',
                                  `${monthShifts.length - 3}`
                                )}
                              </div>
                            )}
                            {monthShifts.length === 0 && (
                              <div className="text-xs text-gray-400">
                                {translate(
                                  'pos.staff.schedule.month_empty_label',
                                  'Click to add shift'
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : viewMode === 'day' ? (
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newDate = new Date(selectedDate);
                          newDate.setDate(newDate.getDate() - 1);
                          setSelectedDate(newDate);
                        }}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        {translate(
                          'pos.staff.schedule.nav_previous',
                          'Previous'
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedDate(new Date())}
                      >
                        {translate('pos.staff.schedule.nav_today', 'Today')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newDate = new Date(selectedDate);
                          newDate.setDate(newDate.getDate() + 1);
                          setSelectedDate(newDate);
                        }}
                      >
                        {translate('pos.staff.schedule.nav_next', 'Next')}
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold">
                      {translate(
                        'pos.staff.schedule.calendar_title_prefix_daily',
                        'Daily Schedule'
                      )}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {selectedDate.toLocaleDateString(undefined, {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                  <div className="space-y-2">
                    {Array.from({ length: 24 }, (_, hour) => {
                      const hourDate = new Date(selectedDate);
                      hourDate.setHours(hour, 0, 0, 0);
                      const hourShifts = getShiftsForDate(hourDate);
                      return (
                        <div
                          key={hour}
                          className="flex items-center border-b py-2 hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            const clickDate = new Date(selectedDate);
                            clickDate.setHours(hour, 0, 0, 0);
                            setSelectedDate(clickDate);
                            setShowCreateDialog(true);
                          }}
                        >
                          <div className="w-16 text-sm text-gray-500">
                            {hour.toString().padStart(2, '0')}:00
                          </div>
                          <div className="flex-1">
                            {hourShifts.length > 0 ? (
                              <div className="space-y-1">
                                {hourShifts.map((shift) => (
                                  <div
                                    key={shift.id}
                                    className={`p-2 rounded text-sm cursor-pointer ${shift.userId === currentUser?.id ? 'bg-green-100 hover:bg-green-200 border border-green-300' : 'bg-blue-50 hover:bg-blue-100'}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingShift(shift);
                                      setShowEditDialog(true);
                                    }}
                                  >
                                    {shift.userName}{shift.userId === currentUser?.id ? ' (You)' : ''} - {shift.startTime}{' '}
                                    {translate('pos.staff.schedule.to', 'to')}{' '}
                                    {shift.endTime}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-gray-400 text-sm">
                                {translate(
                                  'pos.staff.schedule.click_to_add_shift',
                                  'Click to add shift'
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : viewMode === 'week' ? (
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newDate = new Date(selectedDate);
                          newDate.setDate(newDate.getDate() - 7);
                          setSelectedDate(newDate);
                        }}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        {translate(
                          'pos.staff.schedule.nav_previous_week',
                          'Previous Week'
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedDate(new Date())}
                      >
                        {translate(
                          'pos.staff.schedule.nav_this_week',
                          'This Week'
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newDate = new Date(selectedDate);
                          newDate.setDate(newDate.getDate() + 7);
                          setSelectedDate(newDate);
                        }}
                      >
                        {translate(
                          'pos.staff.schedule.nav_next_week',
                          'Next Week'
                        )}
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold">
                      {translate(
                        'pos.staff.schedule.week_view_title',
                        'Week View'
                      )}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {translate(
                        'pos.staff.schedule.week_view_subtitle',
                        'Week of %{date}'
                      ).replace(
                        '%{date}',
                        getWeekStart(selectedDate).toLocaleDateString(undefined)
                      )}
                    </p>
                  </div>
                  <div className="grid grid-cols-7 gap-2">
                    {Array.from({ length: 7 }, (_, i) => {
                      const dayDate = new Date(getWeekStart(selectedDate));
                      dayDate.setDate(dayDate.getDate() + i);
                      const dayShifts = getShiftsForDate(dayDate);
                      return (
                        <div
                          key={i}
                          className="border rounded-lg p-2 hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            setSelectedDate(dayDate);
                            setShowCreateDialog(true);
                          }}
                        >
                          <div className="text-sm font-medium mb-2">
                            {dayDate.toLocaleDateString(undefined, {
                              weekday: 'short',
                              day: 'numeric',
                            })}
                          </div>
                          <div className="space-y-1">
                            {dayShifts.slice(0, 3).map((shift) => (
                              <div
                                key={shift.id}
                                className={`text-xs p-1 rounded cursor-pointer ${shift.userId === currentUser?.id ? 'bg-green-100 hover:bg-green-200 border border-green-300' : 'bg-green-50 hover:bg-green-100'}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditShift(shift);
                                }}
                              >
                                {shift.userName}{shift.userId === currentUser?.id ? ' (You)' : ''}
                                <br />
                                {shift.startTime} - {shift.endTime}
                              </div>
                            ))}
                            {dayShifts.length > 3 && (
                              <div className="text-xs text-gray-500">
                                {translate(
                                  'pos.staff.schedule.month_more_label',
                                  '+%{count} more'
                                ).replace(
                                  '%{count}',
                                  `${dayShifts.length - 3}`
                                )}
                              </div>
                            )}
                            {dayShifts.length === 0 && (
                              <div className="text-xs text-gray-400">
                                {translate(
                                  'pos.staff.schedule.day_add_shift',
                                  'Click to add shift'
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <AppleCalendar
                  selectedDate={selectedDate}
                  onDateSelect={(date) => {
                    setSelectedDate(date);
                    setShowCreateDialog(true);
                  }}
                  getEventsForDate={(date) =>
                    getShiftsForDate(date).map((shift) => {
                      // Check if this shift has a conflict
                      const hasConflict = conflicts.some(
                        (conflict) => conflict.shiftId === shift.id
                      );
                      return {
                        id: shift.id,
                        title: shift.userName,
                        time: `${shift.startTime} - ${shift.endTime}`,
                        status: shift.status as
                          | 'confirmed'
                          | 'scheduled'
                          | 'cancelled'
                          | 'pending',
                        hasConflict,
                        isCurrentUser: shift.userId === currentUser?.id,
                      };
                    })
                  }
                  onEventClick={(event) => {
                    const shift = shifts.find((s) => s.id === event.id);
                    if (shift) {
                      handleEditShift(shift);
                    }
                  }}
                  onShowMoreClick={(date) => {
                    // Set the selected date to show all shifts in the sidepanel
                    setSelectedDate(date);
                    // Scroll to the sidepanel if needed
                    setTimeout(() => {
                      const sidepanel = document.querySelector('[data-sidepanel="date-details"]');
                      if (sidepanel) {
                        sidepanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 100);
                  }}
                  onPreviousMonth={() => {
                    const newDate = new Date(selectedDate);
                    newDate.setMonth(newDate.getMonth() - 1);
                    setSelectedDate(newDate);
                  }}
                  onNextMonth={() => {
                    const newDate = new Date(selectedDate);
                    newDate.setMonth(newDate.getMonth() + 1);
                    setSelectedDate(newDate);
                  }}
                  onToday={() => setSelectedDate(new Date())}
                />
              )}
            </div>

            {/* Selected Date Details */}
            <div className="space-y-4" data-sidepanel="date-details">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {formatDate(selectedDate.toISOString())}
                  </CardTitle>
                  <CardDescription>
                    {translate(
                      'pos.staff.schedule.shifts_scheduled_count',
                      '%{count} shift(s) scheduled'
                    ).replace(
                      '%{count}',
                      `${getShiftsForDate(selectedDate).length}`
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {getShiftsForDate(selectedDate).map((shift) => {
                      // Check if this shift has a conflict
                      const hasConflict = conflicts.some(
                        (conflict) => conflict.shiftId === shift.id
                      );
                      return (
                      <div
                        key={shift.id}
                        className={`p-3 border rounded-lg hover:bg-muted/50 cursor-pointer ${
                          hasConflict
                            ? 'bg-red-50 border-red-500 border-2'
                            : shift.userId === currentUser?.id
                            ? 'bg-green-50 border-green-500 border-2 shadow-sm'
                            : ''
                        }`}
                        onClick={() => handleEditShift(shift)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(shift.status)}
                            <div>
                              <div className="font-medium text-sm flex items-center gap-2">
                                {shift.userName}
                                {shift.userId === currentUser?.id && (
                                  <Badge className="bg-green-600 hover:bg-green-700 h-5 text-[10px] px-1.5">You</Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {shift.position} • {shift.location}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium">
                              {formatTime(shift.startTime)} -{' '}
                              {formatTime(shift.endTime)}
                            </div>
                            <Badge className={getStatusColor(shift.status)}>
                              {shift.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                    {getShiftsForDate(selectedDate).length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <CalendarIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>
                          {translate(
                            'pos.staff.schedule.day_empty_label',
                            'No shifts scheduled'
                          )}
                        </p>
                        <Button
                          size="sm"
                          className="mt-2"
                          onClick={() => {
                            setShiftForm((prev) => ({
                              ...prev,
                              date: selectedDate.toISOString().split('T')[0],
                            }));
                            setShowCreateDialog(true);
                          }}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          {translate(
                            'pos.staff.schedule.day_add_shift',
                            'Add Shift'
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {translate(
                      'pos.staff.schedule.quick_stats_title',
                      'Quick Stats'
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">
                        {shifts.filter((s) => s.status === 'scheduled').length}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {translate(
                          'pos.staff.schedule.quick_stats_scheduled_label',
                          'Scheduled'
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">
                        {shifts.filter((s) => s.status === 'confirmed').length}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {translate(
                          'pos.staff.schedule.quick_stats_confirmed_label',
                          'Confirmed'
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-orange-600">
                        {conflicts.length}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {translate(
                          'pos.staff.schedule.quick_stats_conflicts_label',
                          'Conflicts'
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">
                        {staffMembers.length}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {translate(
                          'pos.staff.schedule.quick_stats_staff_label',
                          'Staff'
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Shift Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>
              {translate(
                'pos.staff.shift_planning.create_title',
                'Create New Shift'
              )}
            </DialogTitle>
            <DialogDescription>
              {translate(
                'pos.staff.shift_planning.create_description',
                'Create a new shift. Staff selection is optional - unassigned shifts will be available for staff to pick up.'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="staff">
                  {translate(
                    'pos.staff.shift_planning.staff_label',
                    'Staff Member (Optional)'
                  )}
                </Label>
                <Popover
                  open={staffSearchOpen}
                  onOpenChange={setStaffSearchOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={staffSearchOpen}
                      className="w-full justify-between"
                    >
                      {shiftForm.userId
                        ? roleFilteredStaffMembers.find(
                            (staff) => staff.id === shiftForm.userId
                          )?.name || 'Select staff member...'
                        : translate(
                            'pos.staff.shift_planning.staff_placeholder',
                            'Select staff member...'
                          )}
                      <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput
                        placeholder={translate(
                          'pos.staff.shift_planning.search_staff',
                          'Search staff members...'
                        )}
                        value={staffSearchValue}
                        onValueChange={setStaffSearchValue}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {translate(
                            'pos.staff.shift_planning.no_staff_found',
                            'No staff members found.'
                          )}
                        </CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="__none__"
                            onSelect={() => {
                              setShiftForm((prev) => ({
                                ...prev,
                                userId: '',
                                position: '',
                              }));
                              setStaffSearchOpen(false);
                              setStaffSearchValue('');
                            }}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium text-muted-foreground">
                                {translate(
                                  'pos.staff.shift_planning.no_staff',
                                  'No staff (Available for pickup)'
                                )}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {translate(
                                  'pos.staff.shift_planning.no_staff_description',
                                  'Leave unassigned for staff to claim'
                                )}
                              </span>
                            </div>
                          </CommandItem>
                          {filteredStaffMembers.map((staff) => (
                            <CommandItem
                              key={staff.id}
                              value={`${staff.name} ${staff.email} ${staff.position}`}
                              onSelect={() => {
                                setShiftForm((prev) => ({
                                  ...prev,
                                  userId: staff.id,
                                  position: staff.position,
                                }));
                                setStaffSearchOpen(false);
                                setStaffSearchValue('');
                              }}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {staff.name}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {staff.email} • {staff.position}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label htmlFor="date">
                  {translate('pos.staff.shift_planning.date_label', 'Date *')}
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={shiftForm.date}
                  onChange={(e) =>
                    setShiftForm((prev) => ({ ...prev, date: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="startTime">
                  {translate(
                    'pos.staff.shift_planning.start_time_label',
                    'Start Time *'
                  )}
                </Label>
                <Input
                  id="startTime"
                  type="time"
                  value={shiftForm.startTime}
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      startTime: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="endTime">
                  {translate(
                    'pos.staff.shift_planning.end_time_label',
                    'End Time *'
                  )}
                </Label>
                <Input
                  id="endTime"
                  type="time"
                  value={shiftForm.endTime}
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      endTime: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="position">
                  {translate(
                    'pos.staff.shift_planning.position_label',
                    'Position (Optional)'
                  )}
                </Label>
                <Select
                  value={shiftForm.position}
                  onValueChange={(value) =>
                    setShiftForm((prev) => ({ ...prev, position: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={translate(
                        'pos.staff.shift_planning.position_placeholder',
                        'Select position'
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Barista">Barista</SelectItem>
                    <SelectItem value="Cashier">Cashier</SelectItem>
                    <SelectItem value="Waiter">Waiter</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="location">
                  {translate(
                    'pos.staff.shift_planning.location_label',
                    'Location (Optional)'
                  )}
                </Label>
                <Input
                  id="location"
                  value={shiftForm.location}
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      location: e.target.value,
                    }))
                  }
                  placeholder={translate(
                    'pos.staff.shift_planning.location_placeholder',
                    'Enter location'
                  )}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes">
                {translate('pos.staff.shift_planning.notes_label', 'Notes')}
              </Label>
              <Textarea
                id="notes"
                value={shiftForm.notes}
                onChange={(e) =>
                  setShiftForm((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder={translate(
                  'pos.staff.shift_planning.notes_placeholder',
                  'Additional notes for this shift...'
                )}
                rows={3}
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleCreateShift} className="flex-1">
                {translate(
                  'pos.staff.shift_planning.create_button',
                  'Create Shift'
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                className="flex-1"
              >
                {translate('pos.common.action.cancel', 'Cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Shift Dialog */}
      <Dialog
        open={showEditDialog}
        onOpenChange={(open) => {
          setShowEditDialog(open);
          if (!open) {
            setEditingShift(null);
            // Clean up any lingering overlays after dialog closes
            setTimeout(() => {
              const overlays = document.querySelectorAll(
                '[data-radix-dialog-overlay], [data-radix-alert-dialog-overlay]'
              );
              overlays.forEach((overlay) => {
                const state = (overlay as HTMLElement).getAttribute(
                  'data-state'
                );
                if (!state || state === 'closed') {
                  overlay.remove();
                }
              });
            }, 200);
          }
        }}
      >
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>
              {translate('pos.staff.shift_planning.edit_title', 'Edit Shift')}
            </DialogTitle>
            <DialogDescription>
              {translate(
                'pos.staff.shift_planning.edit_description',
                'Update shift details'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-staff">
                  {translate(
                    'pos.staff.shift_planning.staff_label',
                    'Staff Member (Optional)'
                  )}
                </Label>
                <Popover
                  open={editStaffSearchOpen}
                  onOpenChange={setEditStaffSearchOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={editStaffSearchOpen}
                      className="w-full justify-between"
                    >
                      {shiftForm.userId
                        ? roleFilteredStaffMembers.find(
                            (staff) => staff.id === shiftForm.userId
                          )?.name || 'Select staff member...'
                        : translate(
                            'pos.staff.shift_planning.staff_placeholder',
                            'Select staff member...'
                          )}
                      <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput
                        placeholder={translate(
                          'pos.staff.shift_planning.search_staff',
                          'Search staff members...'
                        )}
                        value={editStaffSearchValue}
                        onValueChange={setEditStaffSearchValue}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {translate(
                            'pos.staff.shift_planning.no_staff_found',
                            'No staff members found.'
                          )}
                        </CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="__none__"
                            onSelect={() => {
                              setShiftForm((prev) => ({
                                ...prev,
                                userId: '',
                                position: '',
                              }));
                              setEditStaffSearchOpen(false);
                              setEditStaffSearchValue('');
                            }}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium text-muted-foreground">
                                {translate(
                                  'pos.staff.shift_planning.no_staff',
                                  'No staff (Available for pickup)'
                                )}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {translate(
                                  'pos.staff.shift_planning.no_staff_description',
                                  'Leave unassigned for staff to claim'
                                )}
                              </span>
                            </div>
                          </CommandItem>
                          {filteredStaffMembersForEdit.map((staff) => (
                            <CommandItem
                              key={staff.id}
                              value={`${staff.name} ${staff.email} ${staff.position}`}
                              onSelect={() => {
                                setShiftForm((prev) => ({
                                  ...prev,
                                  userId: staff.id,
                                  position: staff.position,
                                }));
                                setEditStaffSearchOpen(false);
                                setEditStaffSearchValue('');
                              }}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {staff.name}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {staff.email} • {staff.position}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label htmlFor="edit-date">
                  {translate('pos.staff.shift_planning.date_label', 'Date *')}
                </Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={shiftForm.date}
                  onChange={(e) =>
                    setShiftForm((prev) => ({ ...prev, date: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-startTime">
                  {translate(
                    'pos.staff.shift_planning.start_time_label',
                    'Start Time *'
                  )}
                </Label>
                <Input
                  id="edit-startTime"
                  type="time"
                  value={shiftForm.startTime}
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      startTime: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="edit-endTime">
                  {translate(
                    'pos.staff.shift_planning.end_time_label',
                    'End Time *'
                  )}
                </Label>
                <Input
                  id="edit-endTime"
                  type="time"
                  value={shiftForm.endTime}
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      endTime: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-position">
                  {translate(
                    'pos.staff.shift_planning.position_label',
                    'Position (Optional)'
                  )}
                </Label>
                <Select
                  value={shiftForm.position}
                  onValueChange={(value) =>
                    setShiftForm((prev) => ({ ...prev, position: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={translate(
                        'pos.staff.shift_planning.position_placeholder',
                        'Select position'
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Barista">Barista</SelectItem>
                    <SelectItem value="Cashier">Cashier</SelectItem>
                    <SelectItem value="Waiter">Waiter</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-location">
                  {translate(
                    'pos.staff.shift_planning.location_label',
                    'Location (Optional)'
                  )}
                </Label>
                <Input
                  id="edit-location"
                  value={shiftForm.location}
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      location: e.target.value,
                    }))
                  }
                  placeholder={translate(
                    'pos.staff.shift_planning.location_placeholder',
                    'Enter location'
                  )}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit-notes">
                {translate('pos.staff.shift_planning.notes_label', 'Notes')}
              </Label>
              <Textarea
                id="edit-notes"
                value={shiftForm.notes}
                onChange={(e) =>
                  setShiftForm((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder={translate(
                  'pos.staff.shift_planning.notes_placeholder',
                  'Additional notes for this shift...'
                )}
                rows={3}
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="destructive"
                onClick={() => {
                  if (editingShift) {
                    setShowDeleteDialog(true);
                  }
                }}
                className="flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {translate('pos.staff.shift_planning.delete_button', 'Delete')}
              </Button>
              <Button onClick={handleUpdateShift} className="flex-1">
                {translate(
                  'pos.staff.shift_planning.update_button',
                  'Update Shift'
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditDialog(false);
                  setEditingShift(null);
                  // Clean up any lingering overlays after dialog closes
                  setTimeout(() => {
                    const overlays = document.querySelectorAll(
                      '[data-radix-dialog-overlay], [data-radix-alert-dialog-overlay]'
                    );
                    overlays.forEach((overlay) => {
                      const state = (overlay as HTMLElement).getAttribute(
                        'data-state'
                      );
                      if (!state || state === 'closed') {
                        overlay.remove();
                      }
                    });
                  }, 200);
                }}
                className="flex-1"
              >
                {translate('pos.common.action.cancel', 'Cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open);
          // If closing the delete dialog, make sure to clean up properly
          if (!open) {
            // Small delay to ensure overlay is removed
            setTimeout(() => {
              // Force cleanup of any lingering overlays
              const overlays = document.querySelectorAll(
                '[data-radix-alert-dialog-overlay]'
              );
              overlays.forEach((overlay) => {
                if (
                  !(overlay as HTMLElement).hasAttribute('data-state') ||
                  (overlay as HTMLElement).getAttribute('data-state') ===
                    'closed'
                ) {
                  overlay.remove();
                }
              });
            }, 100);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {translate(
                'pos.staff.shift_planning.delete_confirm_title',
                'Delete Shift'
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {translate(
                'pos.staff.shift_planning.delete_confirm',
                'Are you sure you want to delete this shift? This action cannot be undone.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowDeleteDialog(false);
              }}
            >
              {translate('pos.common.action.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (editingShift) {
                  await handleDeleteShift(editingShift.id);
                  setShowDeleteDialog(false);
                  setShowEditDialog(false);
                  setEditingShift(null);
                  // Force cleanup after a short delay
                  setTimeout(() => {
                    const overlays = document.querySelectorAll(
                      '[data-radix-alert-dialog-overlay], [data-radix-dialog-overlay]'
                    );
                    overlays.forEach((overlay) => {
                      const state = (overlay as HTMLElement).getAttribute(
                        'data-state'
                      );
                      if (!state || state === 'closed') {
                        overlay.remove();
                      }
                    });
                  }, 200);
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {translate('pos.staff.shift_planning.delete_button', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ModernShiftCalendar;
